/**
 * RESPONSIBILITY: the Austin Desi Christmas page — its sections, each with its pencil, on its own colours.
 *
 * Standalone: no site header, no site footer, no menu. It links back to the church and
 * otherwise stands on its own, which is why SitePage hands it the whole screen.
 *
 * THE COLOURS ARE WRITTEN ONTO THIS PAGE'S OWN ELEMENT, never :root. A Christmas red set
 * here must not turn the church's home page red, and the SPA keeps both in one document.
 * An empty field is not written at all, so the page falls back to the site's colour.
 */
import type { CSSProperties } from 'react';
import { SectionName, type AdcThemeSection } from '@app/sections';
import { cms, SectionUpdate } from '../cms';
import { AdcTop, AdcBlocks, AdcFoot } from '../site';

const VARS: Record<keyof AdcThemeSection, string> = {
  primary: '--primary', secondary: '--secondary', olive: '--olive',
  cream: '--cream', mist: '--mist', sand: '--sand',
};

const stylesOf = (t: Partial<AdcThemeSection>): CSSProperties =>
  Object.fromEntries(
    (Object.keys(VARS) as (keyof AdcThemeSection)[])
      .filter((k) => t[k])
      .map((k) => [VARS[k], t[k]]),
  ) as CSSProperties;

export function AdcPage() {
  // The closing invitation repeats the header's button, which is the opening section's to say.
  const top = cms.get(SectionName.AdcTop).data;
  const cta = { label: top?.registerLabel ?? '', href: top?.registerHref ?? '' };

  return (
    <SectionUpdate name={SectionName.AdcTheme} pencil="bottom-right">
      {(t) => (
        <div className="adc-page" style={stylesOf(t)}>
          <SectionUpdate name={SectionName.AdcTop}>{(c) => <AdcTop c={c} />}</SectionUpdate>
          <main>
            <SectionUpdate name={SectionName.AdcBlocks}>
              {(c) => <AdcBlocks blocks={c.blocks} />}
            </SectionUpdate>
          </main>
          <SectionUpdate name={SectionName.AdcFoot}>{(c) => <AdcFoot c={c} cta={cta} />}</SectionUpdate>
        </div>
      )}
    </SectionUpdate>
  );
}
